import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, FolderKanban, Edit, Trash2, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Projects() {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteProjectId, setDeleteProjectId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, {
        withCredentials: true
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteProjectId) return;

    try {
      await axios.delete(`${API}/projects/${deleteProjectId}`, {
        withCredentials: true
      });
      toast.success('Project deleted successfully');
      setDeleteProjectId(null);
      loadProjects();
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-4 text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight mb-2">
              {t('projects.title', 'Projects')}
            </h1>
            <p className="text-lg text-muted-foreground font-body">
              {projects.length} {projects.length === 1 ? t('projects.project', 'project') : t('projects.count', 'projects')}
            </p>
          </div>
          <Button
            data-testid="add-project-button"
            onClick={() => navigate('/projects/add')}
            className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('projects.newProject', 'New Project')}
          </Button>
        </div>

        {projects.length === 0 ? (
          <div className="text-center py-20">
            <FolderKanban className="h-24 w-24 text-muted-foreground/30 mx-auto mb-6" />
            <h3 className="text-2xl font-heading font-semibold mb-2">{t('projects.noProjects', 'No projects yet')}</h3>
            <p className="text-muted-foreground font-body mb-6">
              {t('projects.noProjectsMessage', 'Start tracking your knitting and crochet projects')}
            </p>
            <Button
              data-testid="empty-add-project-button"
              onClick={() => navigate('/projects/add')}
              className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('projects.startFirst', 'Start Your First Project')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project) => (
              <div
                key={project.project_id}
                data-testid={`project-card-${project.project_id}`}
                className="rounded-3xl bg-white border border-stone-100 p-6 relative overflow-hidden group hover:border-rose-100 transition-all hover:shadow-xl cursor-pointer"
                onClick={() => navigate(`/projects/${project.project_id}`)}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-2xl font-heading font-semibold mb-2 truncate">
                      {project.name}
                    </h3>
                    <div className="flex items-center space-x-2">
                      {project.status === 'in_progress' ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-accent-soft text-accent">
                          <Clock className="h-3 w-3 mr-1" />
                          In Progress
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Completed
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {project.description && (
                  <p className="text-sm text-muted-foreground font-body mb-4 line-clamp-2">
                    {project.description}
                  </p>
                )}

                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-medium text-muted-foreground">Progress</span>
                    <span className="text-xs font-bold text-primary">{project.progress_percentage}%</span>
                  </div>
                  <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-300"
                      style={{ width: `${project.progress_percentage}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                  <span className="text-xs text-muted-foreground">
                    {project.yarns_used?.length || 0} {project.yarns_used?.length === 1 ? 'yarn' : 'yarns'}
                  </span>
                  <div className="flex items-center space-x-1">
                    <Button
                      data-testid={`edit-project-${project.project_id}`}
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/projects/${project.project_id}/edit`);
                      }}
                      className="h-8 w-8 p-0 hover:bg-rose-50"
                    >
                      <Edit className="h-4 w-4 text-stone-500 hover:text-primary" />
                    </Button>
                    <Button
                      data-testid={`delete-project-${project.project_id}`}
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteProjectId(project.project_id);
                      }}
                      className="h-8 w-8 p-0 hover:bg-rose-50"
                    >
                      <Trash2 className="h-4 w-4 text-stone-500 hover:text-destructive" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <AlertDialog open={!!deleteProjectId} onOpenChange={() => setDeleteProjectId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this project? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-button"
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
